const YEARS_BACK_LIMIT = 2;
const YEARS_FORWARD_LIMIT = 2;

export type DateValidationResult = {
  readonly isValid: boolean;
  readonly errorMessage?: string;
};

/**
 * Parse optional number from string input
 *
 * Converts a string to a number or null.
 * Used for optional form fields like price, seats, quota.
 *
 * @param value - String value from input field
 * @returns Number or null if empty/invalid
 *
 * @example
 * parseOptionalNumber("299")  // 299
 * parseOptionalNumber("")     // null
 * parseOptionalNumber("abc")  // null
 */
export const parseOptionalNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Validate billing start date
 *
 * Validates a date string is within acceptable range.
 * Dates must be between 2 years in the past and 2 years in the future.
 *
 * @param dateString - ISO date string (YYYY-MM-DD)
 * @returns Validation result with error message if invalid
 *
 * @example
 * validateBillingStartDate("")              // { isValid: true }
 * validateBillingStartDate("2026-01-01")    // { isValid: true }
 * validateBillingStartDate("2020-01-01")    // { isValid: false, errorMessage: "..." }
 */
export const validateBillingStartDate = (dateString: string): DateValidationResult => {
  // Empty is valid (optional field)
  if (!dateString) {
    return { isValid: true };
  }

  const selectedDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time for fair comparison

  // Calculate bounds
  const pastLimit = new Date(today);
  pastLimit.setFullYear(today.getFullYear() - YEARS_BACK_LIMIT);

  const futureLimit = new Date(today);
  futureLimit.setFullYear(today.getFullYear() + YEARS_FORWARD_LIMIT);

  // Validate past limit
  if (selectedDate < pastLimit) {
    return {
      isValid: false,
      errorMessage: `Faktureringstartdato kan ikke være mer enn ${YEARS_BACK_LIMIT} år tilbake i tid`,
    };
  }

  // Validate future limit
  if (selectedDate > futureLimit) {
    return {
      isValid: false,
      errorMessage: `Faktureringstartdato kan ikke være mer enn ${YEARS_FORWARD_LIMIT} år frem i tid`,
    };
  }

  return { isValid: true };
};

/**
 * Convert NOK to øre (cents)
 *
 * Converts Norwegian Kroner to øre.
 * Used when sending prices to backend (which stores in øre).
 *
 * @param nok - Price in NOK
 * @returns Price in øre (1 NOK = 100 øre)
 *
 * @example
 * convertNOKToOre(299)   // 29900
 * convertNOKToOre(299.5) // 29950
 */
export const convertNOKToOre = (nok: number): number => {
  return Math.round(nok * 100);
};

/**
 * Convert øre to NOK
 *
 * Converts øre to Norwegian Kroner.
 * Used when displaying prices from backend (which stores in øre).
 *
 * @param ore - Price in øre
 * @returns Price in NOK (100 øre = 1 NOK)
 *
 * @example
 * convertOreToNOK(29900) // 299
 * convertOreToNOK(29950) // 299.5
 */
export const convertOreToNOK = (ore: number): number => {
  return ore / 100;
};
